import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, Cell, ReferenceLine, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  ChevronLeft, Link2, Check, Scale, Download, HeartPulse, Brain, SlidersHorizontal,
  Baby, Briefcase, TrendingUp, Shirt, Sparkles, Lock, CalendarDays, Fingerprint,
  BookOpen, BrainCircuit, Wallet, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, useSensor, useSensors,
  PointerSensor, TouchSensor, KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { nexaApi, API_ROOT } from './api';
import { HealthCard } from './HealthCard';
import { MedicalSection } from './MedicalSection';
import { WorkoutLogger } from './WorkoutLogger';
import { SupplementsPlanCard } from './SupplementsCard';
import { MemoryView } from './MemoryView';
import { PushCard } from './PushCard';
import { InstallCard } from './InstallCard';
import { GoogleConnectCard } from './GoogleConnectCard';
import { CalendarDetail } from './CalendarDetail';
import { PersonalityDetail } from './PersonalityDetail';
import { StylingDetail } from './StylingDetail';
import { KidsDetail } from './KidsDetail';
import { NexaMindView } from './NexaMindView';
import { SleepSection } from './SleepSection';
import { GrowthDetail } from './GrowthDetail';
import { BusinessDetail } from './BusinessDetail';
import { BusinessLevantaDetail } from './BusinessLevantaDetail';
import { FinanceDetail } from './FinanceDetail';
import { RunsSection } from './RunsSection';
import { JournalDetail } from './JournalDetail';

const SYNC_URL = `${API_ROOT}/api/nexa/health/sync`;

// ---------- shared helpers ----------
const fmtSleep = (h) => `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, '0')}m`;

// ---------- utility cards (unchanged content, restyled slightly) ----------
const CopySyncUrl = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SYNC_URL);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = SYNC_URL;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] px-4 py-3.5" data-testid="nexa-sync-url-card">
      <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-2">
        <Link2 className="w-3 h-3" /> Shortcut sync URL
      </div>
      <p className="text-[11px] text-white/60 font-mono break-all leading-relaxed">{SYNC_URL}</p>
      <button
        onClick={copy}
        data-testid="nexa-copy-sync-url"
        className={`mt-3 w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${
          copied ? 'bg-emerald-400 text-black' : 'bg-white text-black'
        }`}
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" strokeWidth={2.5} /> Copied — paste into your Shortcut
          </>
        ) : (
          'Copy sync URL'
        )}
      </button>
    </div>
  );
};

const NutritionBar = ({ label, val, max, unit }) => (
  <div className="mb-3">
    <div className="flex justify-between text-[11px] text-white/50 mb-1">
      <span>{label}</span>
      <span>{val}/{max}{unit}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
      <div className="h-full bg-sky-400 rounded-full" style={{ width: `${Math.min((val / max) * 100, 100)}%` }} />
    </div>
  </div>
);

const NutritionChart = ({ history }) => {
  if (!history || !history.days || history.days.length === 0) return null;
  const target = history.targets || {};
  const rows = history.days.map((d) => ({
    label: d.date.slice(5),
    kcal: d.kcal,
    protein: d.protein_g,
    hitProtein: target.protein_g ? d.protein_g >= 0.9 * target.protein_g : false,
    logged: d.meals > 0,
  }));
  const loggedRows = rows.filter((r) => r.logged);
  const proteinHits = loggedRows.filter((r) => r.hitProtein).length;
  const kcalHits = loggedRows.filter(
    (r) => target.kcal && Math.abs(r.kcal - target.kcal) <= 0.1 * target.kcal,
  ).length;
  const avgKcal = loggedRows.length
    ? Math.round(loggedRows.reduce((a, b) => a + b.kcal, 0) / loggedRows.length)
    : 0;
  const avgProtein = loggedRows.length
    ? Math.round(loggedRows.reduce((a, b) => a + b.protein, 0) / loggedRows.length)
    : 0;
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-nutrition-chart">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs tracking-[0.2em] uppercase text-white/40">Last 7 days</span>
        <span className="text-[10px] text-white/40 tracking-wide" data-testid="nexa-nutrition-chart-summary">
          {loggedRows.length}/7 logged · avg {avgKcal} kcal · {avgProtein}g protein
        </span>
      </div>
      <div className="h-40 mt-3 -ml-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} barCategoryGap={6}>
            <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} width={34} />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff', fontSize: 12 }}
              formatter={(v, name, props) => {
                if (name === 'kcal') return [`${v} kcal · ${props.payload.protein}g protein`, props.payload.label];
                return [v, name];
              }}
              labelFormatter={() => ''}
            />
            {target.kcal ? (
              <ReferenceLine y={target.kcal} stroke="rgba(56,189,248,0.5)" strokeDasharray="4 4"
                label={{ value: `target ${target.kcal}`, position: 'insideTopRight', fill: 'rgba(56,189,248,0.7)', fontSize: 9 }} />
            ) : null}
            <Bar dataKey="kcal" radius={[6, 6, 0, 0]}>
              {rows.map((r, i) => (
                <Cell key={i} fill={r.hitProtein ? '#38BDF8' : r.logged ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 text-[10px] text-white/50">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-400" /> protein hit</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-white/25" /> logged</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-white/10" /> no log</span>
        </div>
        <span className="text-[10px] text-white/50" data-testid="nexa-nutrition-chart-hits">
          {kcalHits}/7 kcal · {proteinHits}/7 protein
        </span>
      </div>
    </div>
  );
};

const WeightWeekChart = ({ weights }) => {
  const cutoff = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const rows = (weights || [])
    .filter((w) => w.date >= cutoff)
    .map((w) => ({ label: w.date.slice(5), weight: w.weight }));
  if (rows.length === 0) return null;
  const avg = rows.reduce((a, b) => a + b.weight, 0) / rows.length;
  const delta = rows[rows.length - 1].weight - rows[0].weight;
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-weight-week-chart">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs tracking-[0.2em] uppercase text-white/40">Weight · last 7 days</span>
        <span className="text-[10px] text-white/40 tracking-wide" data-testid="nexa-weight-week-summary">
          {rows.length}/7 logged · avg {avg.toFixed(1)} kg · {delta >= 0 ? '+' : ''}{delta.toFixed(1)} kg
        </span>
      </div>
      <div className="h-40 mt-3 -ml-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis domain={['dataMin - 0.5', 'dataMax + 0.5']} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }}
              axisLine={false} tickLine={false} width={34} tickFormatter={(v) => Number(v).toFixed(1)} />
            <Tooltip
              cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
              contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff', fontSize: 12 }}
              formatter={(v) => [`${v} kg`, 'Weight']}
              labelFormatter={() => ''}
            />
            <ReferenceLine y={avg} stroke="rgba(56,189,248,0.5)" strokeDasharray="4 4"
              label={{ value: `avg ${avg.toFixed(1)}`, position: 'insideTopRight', fill: 'rgba(56,189,248,0.7)', fontSize: 9 }} />
            <Line type="monotone" dataKey="weight" stroke="#38BDF8" strokeWidth={2} dot={{ fill: '#38BDF8', r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.06]">
        <span className="flex items-center gap-1.5 text-[10px] text-white/50">
          <span className="w-2 h-2 rounded-full bg-sky-400" /> daily weigh-in
        </span>
        <span className="text-[10px] text-white/50">dashed line = 7-day average</span>
      </div>
    </div>
  );
};

const NutritionCard = ({ nutrition }) => {
  if (!nutrition) return null;
  const { totals, targets, meals } = nutrition;
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-nutrition-card">
      <span className="text-xs tracking-[0.2em] uppercase text-white/40">Today&apos;s intake</span>
      <div className="mt-4">
        <NutritionBar label="Calories" val={totals.kcal} max={targets.kcal} unit=" kcal" />
        <NutritionBar label="Protein" val={totals.protein_g} max={targets.protein_g} unit="g" />
        <NutritionBar label="Carbs" val={totals.carbs_g} max={targets.carbs_g} unit="g" />
        <NutritionBar label="Fat" val={totals.fat_g} max={targets.fat_g} unit="g" />
      </div>
      {meals.length === 0 ? (
        <p className="text-xs text-white/40 mt-2">No meals yet — send NEXA a meal photo in chat.</p>
      ) : (
        meals.map((m) => (
          <p key={m.id} className="text-xs text-white/50 mt-1.5">
            {m.items.join(', ')} — {m.kcal} kcal · {m.protein_g}g protein
          </p>
        ))
      )}
    </div>
  );
};

const EX_COLORS = { 'Pull-ups': '#38BDF8', Dips: '#FB923C', 'Push-ups': '#A78BFA' };

const ProgressionChart = ({ workouts }) => {
  const rows = workouts
    .slice()
    .reverse()
    .filter((w) => w.exercises && w.exercises.length)
    .map((w) => {
      const r = { date: w.date.slice(5) };
      w.exercises.forEach((e) => {
        const n = (e.name || '').toLowerCase();
        const total = (e.sets || []).reduce((a, b) => a + b, 0);
        if (n.includes('pull')) r['Pull-ups'] = total;
        else if (n.includes('dip')) r.Dips = total;
        else if (n.includes('push')) r['Push-ups'] = total;
      });
      return r;
    });
  if (rows.length === 0) return null;
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-progression-chart">
      <span className="text-xs tracking-[0.2em] uppercase text-white/40">Progression · total reps</span>
      <div className="h-48 mt-4 -ml-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
            <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff', fontSize: 12 }} />
            {Object.entries(EX_COLORS).map(([k, c]) => (
              <Line key={k} type="monotone" dataKey={k} stroke={c} strokeWidth={2} dot={{ fill: c, r: 3 }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-1">
        {Object.entries(EX_COLORS).map(([k, c]) => (
          <span key={k} className="text-[10px] text-white/50 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} /> {k}
          </span>
        ))}
      </div>
    </div>
  );
};

const WorkoutsCard = ({ workouts }) => (
  <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-workouts-card">
    <div className="px-6 pt-5 pb-2">
      <span className="text-xs tracking-[0.2em] uppercase text-white/40">Workout history</span>
    </div>
    {workouts.length === 0 ? (
      <p className="text-sm text-white/40 px-6 pb-6 pt-2">
        No workouts yet — just tell NEXA in chat what you did (&ldquo;pull-ups 7/5/3…&rdquo;) and she logs it here.
      </p>
    ) : (
      workouts.map((w, idx) => (
        <div
          key={w.id}
          className={`px-6 py-4 ${idx < workouts.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
          data-testid={`nexa-workout-${idx}`}
        >
          <p className="text-sm font-medium">
            {new Date(w.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
          </p>
          <p className="text-xs text-white/60 mt-1">{w.summary}</p>
          {Array.isArray(w.exercises) && w.exercises.length > 0 && (
            <div className="flex flex-col gap-1 mt-2">
              {w.exercises.map((ex, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px]" data-testid={`nexa-workout-ex-${idx}-${i}`}>
                  <span className="text-white/70 w-20 shrink-0">{ex.name}</span>
                  <span className="flex flex-wrap gap-1">
                    {(ex.sets || []).map((s, j) => (
                      <span key={j} className="px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 text-[11px]">{s}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
          {w.cardio && (
            <p className="text-xs text-white/50 mt-0.5">
              {w.cardio.km ? `${w.cardio.km} km` : ''} {w.cardio.time ? `in ${w.cardio.time}` : ''}
            </p>
          )}
          {w.notes && <p className="text-[11px] text-white/35 mt-0.5 italic">{w.notes}</p>}
        </div>
      ))
    )}
  </div>
);

const ExportBackup = () => {
  const [busy, setBusy] = useState(false);
  const doExport = async () => {
    setBusy(true);
    try {
      const { data } = await nexaApi.get('/soul/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `nexa-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] px-4 py-3.5" data-testid="nexa-export-card">
      <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-2">
        <Download className="w-3 h-3" /> NEXA backup
      </div>
      <p className="text-xs text-white/50 leading-relaxed">
        Her soul, memories, profile and all your data in one file — so she can be restored anywhere.
      </p>
      <button
        onClick={doExport}
        disabled={busy}
        data-testid="nexa-export-backup"
        className="mt-3 w-full h-10 rounded-xl text-sm font-semibold bg-white text-black disabled:opacity-50"
      >
        {busy ? 'Preparing…' : 'Export NEXA backup'}
      </button>
    </div>
  );
};

// ---------- detail views ----------
const HealthFitnessDetail = ({ today, days, weights, workouts, nutrition, nutritionHistory, reloadWorkouts }) => {
  const week = days.slice(0, 7).map((d) => d.metrics || {});
  const currentWeight = today.weight_today ?? today.weight_latest;
  const [tab, setTab] = useState('nutrition');
  const TABS = [['nutrition', 'Nutrition'], ['training', 'Training'], ['running', 'Running'], ['sleep', 'Sleep'], ['health', 'Health']];
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-1 p-1 rounded-2xl bg-white/[0.04] border border-white/10 lg:max-w-xl" data-testid="nexa-health-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`nexa-health-tab-${v}`}
            className={`flex-1 min-w-[72px] h-9 px-3 rounded-xl text-[12.5px] font-medium transition-colors ${tab === v ? 'bg-sky-400 text-black' : 'text-white/55 hover:text-white/80'}`}>{l}</button>
        ))}
      </div>

      {tab === 'nutrition' && (
        <div className="flex flex-col gap-8" data-testid="nexa-health-page-nutrition">
          <section>
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">Nutrition</h2>
            <div className="flex flex-col gap-4">
              <NutritionChart history={nutritionHistory} />
              <NutritionCard nutrition={nutrition} />
            </div>
          </section>

          <section>
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">Weight</h2>
            <div className="flex flex-col gap-4">
              <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-weight-card">
                <div className="flex items-center gap-2 mb-4">
                  <Scale className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
                  <span className="text-xs tracking-[0.2em] uppercase text-white/40">Weight</span>
                </div>
                <div className="nexa-heading text-5xl font-light" data-testid="nexa-weight-current">
                  {currentWeight != null ? currentWeight : '—'}
                  {currentWeight != null && <span className="text-lg text-white/40 ml-1">kg</span>}
                </div>
                <p className="text-xs text-white/40 mt-1">
                  {today.weight_today != null ? 'Logged today' : currentWeight != null ? 'Last recorded' : 'No entries yet'}
                </p>
                <p className="text-xs text-white/30 mt-3">
                  Just tell NEXA your weight in chat — she logs it here automatically.
                </p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-weight-chart">
                <span className="text-xs tracking-[0.2em] uppercase text-white/40">Weight trend</span>
                {weights.length >= 1 ? (
                  <div className="h-48 mt-4 -ml-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={weights}>
                        <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }}
                          tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                        <YAxis domain={['dataMin - 1', 'dataMax + 1']}
                          tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }}
                          axisLine={false} tickLine={false} width={40} />
                        <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 12, color: '#fff', fontSize: 12 }} formatter={(v) => [`${v} kg`, 'Weight']} />
                        <Line type="monotone" dataKey="weight" stroke="#38BDF8" strokeWidth={2}
                          dot={{ fill: '#38BDF8', r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-white/40 mt-4">Log your weight for a few days to see the trend.</p>
                )}
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">Supplements</h2>
            <SupplementsPlanCard />
          </section>
        </div>
      )}

      {tab === 'training' && (
        <div className="flex flex-col gap-8" data-testid="nexa-health-page-training">
          <section>
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">Training</h2>
            <div className="flex flex-col gap-4">
              <WorkoutLogger onSaved={reloadWorkouts} />
              <ProgressionChart workouts={workouts} />
              <WorkoutsCard workouts={workouts} />
            </div>
          </section>
        </div>
      )}

      {tab === 'running' && (
        <div className="flex flex-col gap-8" data-testid="nexa-health-page-running">
          <RunsSection />
        </div>
      )}

      {tab === 'sleep' && (
        <div className="flex flex-col gap-8" data-testid="nexa-health-page-sleep">
          <SleepSection days={days} />
        </div>
      )}

      {tab === 'health' && (
        <div className="flex flex-col gap-8" data-testid="nexa-health-page-vitals">
          <section>
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">Vitals</h2>
            <div className="flex flex-col gap-4">
              <HealthCard health={today.health} updatedAt={today.health_updated_at} />
              <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-health-history">
                <div className="px-6 pt-5 pb-2">
                  <span className="text-xs tracking-[0.2em] uppercase text-white/40">Health history · 30 days</span>
                </div>
                {days.length === 0 && (
                  <p className="text-sm text-white/40 px-6 pb-6 pt-2">No health data synced yet.</p>
                )}
                {days.map((d, idx) => {
                  const m = d.metrics || {};
                  const bits = [
                    m.sleep_hours != null ? `${fmtSleep(Number(m.sleep_hours))} sleep` : null,
                    m.steps != null ? `${Number(m.steps).toLocaleString()} steps` : null,
                    m.active_energy_kcal != null ? `${Math.round(m.active_energy_kcal)} kcal` : null,
                    m.resting_hr != null ? `${m.resting_hr} bpm` : null,
                    m.walking_km != null ? `${Number(m.walking_km).toFixed(1)} km` : null,
                  ].filter(Boolean);
                  return (
                    <div
                      key={d.date}
                      className={`px-6 py-4 ${idx < days.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
                      data-testid={`nexa-health-day-${d.date}`}
                    >
                      <p className="text-sm font-medium">
                        {new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', {
                          weekday: 'short', day: 'numeric', month: 'short',
                        })}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5">{bits.join(' · ') || 'no metrics'}</p>
                    </div>
                  );
                })}
              </div>
              {week.length > 0 && (
                <p className="text-[11px] text-white/30 px-1">7-day snapshot lives at the top of your chat.</p>
              )}
            </div>
          </section>

          <MedicalSection />
        </div>
      )}
    </div>
  );
};

const SettingsDetail = () => (
  <div className="flex flex-col gap-4">
    <InstallCard />
    <GoogleConnectCard />
    <PushCard />
    <CopySyncUrl />
    <ExportBackup />
  </div>
);

// ---------- skill definitions ----------
const buildSkills = ({ today, weights, days, workouts, memoryCount, calendar, personality, wardrobeCount, kids }) => {
  const currentWeight = today?.weight_today ?? today?.weight_latest;
  const latestSleep = days?.[0]?.metrics?.sleep_hours;
  const healthStat = [
    currentWeight != null ? `${currentWeight} kg` : null,
    latestSleep != null ? `${fmtSleep(Number(latestSleep))} sleep` : null,
    (workouts || []).length ? `${workouts.length} workouts` : null,
  ].filter(Boolean).join(' · ') || 'No data yet — talk to NEXA';

  const calendarStat = calendar?.connected
    ? (calendar.events?.length
        ? `${calendar.events.length} events · next 30 days`
        : 'No upcoming events')
    : 'Connect in Settings';

  return [
    { id: 'health', name: 'Health & Fitness', icon: HeartPulse, unlocked: true, stat: healthStat },
    { id: 'calendar', name: 'Calendar', icon: CalendarDays, unlocked: true, stat: calendarStat },
    {
      id: 'memory', name: 'Memory', icon: Brain, unlocked: true,
      stat: memoryCount ? `${memoryCount} memories learned` : 'Nothing learned yet',
    },
    {
      id: 'personality', name: 'Personality', icon: Fingerprint, unlocked: true,
      stat: personality?.mbti
        ? `${personality.mbti} · ${(personality.archetypes || []).length} archetypes`
        : 'Talk to NEXA about yourself',
    },
    {
      id: 'nexa', name: "NEXA's Mind", icon: BrainCircuit, unlocked: true,
      stat: 'Her personality · emotions · your bond',
    },
    { id: 'settings', name: 'Settings', icon: SlidersHorizontal, unlocked: true, stat: 'Install · Push · Sync · Backup' },
    {
      id: 'kids', name: 'Kids', icon: Baby, unlocked: true,
      stat: (kids || []).length
        ? (kids || []).map((k) => `${k.name} (${k.age})`).join(' · ')
        : 'Set up profiles',
    },
    { id: 'work', name: 'Work', icon: Briefcase, unlocked: false },
    {
      id: 'business', name: 'Business', icon: TrendingUp, unlocked: true,
      stat: 'Levanta · Evolvex · The Eilati',
    },
    {
      id: 'finance', name: 'Finance', icon: Wallet, unlocked: true,
      stat: 'Bank & receipts · income, expenses, leaks',
    },
    {
      id: 'journal', name: 'Journal', icon: BookOpen, unlocked: true,
      stat: 'Mood · Photos · Places · NEXA learns from every entry',
    },
    {
      id: 'styling', name: 'Styling', icon: Shirt, unlocked: true,
      stat: wardrobeCount ? `${wardrobeCount} items · quiet luxury` : 'Send an outfit photo',
    },
    { id: 'personal', name: 'Personal Growth', icon: Sparkles, unlocked: true, stat: 'Skills · goals · plans' },
  ];
};

// ---------- skill tile ----------
const SkillTile = ({ skill, onSelect, dragging, isFav, onToggleFav }) => {
  const Icon = skill.icon;
  const locked = !skill.unlocked;
  return (
    <motion.button
      whileTap={{ scale: dragging ? 1 : 0.97 }}
      onClick={() => onSelect(skill)}
      data-testid={`nexa-skill-${skill.id}`}
      className={`relative aspect-square lg:aspect-auto lg:h-48 w-full rounded-2xl border p-4 lg:p-5 text-left flex flex-col justify-between transition-all bg-[#0A0A0A] ${
        locked
          ? 'border-white/[0.08] opacity-60'
          : 'border-white/10 hover:border-sky-400/50 hover:shadow-[0_0_20px_rgba(56,189,248,0.15)]'
      } ${dragging ? 'shadow-[0_0_30px_rgba(56,189,248,0.35)] border-sky-400/70' : ''} ${
        isFav ? 'border-amber-400/40' : ''
      }`}
    >
      {/* Fallout-style corner brackets */}
      <span className={`absolute top-1.5 left-1.5 w-2.5 h-2.5 border-t border-l ${locked ? 'border-white/15' : 'border-sky-400/50'}`} />
      <span className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 border-t border-r ${locked ? 'border-white/15' : 'border-sky-400/50'}`} />
      <span className={`absolute bottom-1.5 left-1.5 w-2.5 h-2.5 border-b border-l ${locked ? 'border-white/15' : 'border-sky-400/50'}`} />
      <span className={`absolute bottom-1.5 right-1.5 w-2.5 h-2.5 border-b border-r ${locked ? 'border-white/15' : 'border-sky-400/50'}`} />

      <div className="flex items-start justify-between">
        {locked ? (
          <Lock className="w-5 h-5 text-white/25" strokeWidth={1.5} />
        ) : (
          <Icon className="w-6 h-6 text-sky-400" strokeWidth={1.4} />
        )}
        {locked ? (
          <span className="text-[9px] font-mono tracking-[0.2em] text-white/25">LOCKED</span>
        ) : (
          <span
            role="button" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onToggleFav(skill.id); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggleFav(skill.id); } }}
            data-testid={`nexa-skill-fav-${skill.id}`}
            aria-label={isFav ? 'Unfavorite' : 'Favorite'}
            className="p-1 -m-1 cursor-pointer"
          >
            <Star className={`w-4 h-4 ${isFav ? 'text-amber-400 fill-amber-400' : 'text-white/20'}`} strokeWidth={1.8} />
          </span>
        )}
      </div>

      <div>
        <div className={`text-[11px] tracking-[0.18em] font-mono uppercase leading-tight ${
          locked ? 'text-white/40' : 'text-white/85'
        }`}>
          {skill.name}
        </div>
        {!locked && skill.stat && (
          <div className="text-[10px] text-white/40 mt-1.5 font-mono leading-snug line-clamp-2">
            {skill.stat}
          </div>
        )}
        {locked && (
          <div className="text-[10px] text-white/25 mt-1.5 font-mono">— not tracked —</div>
        )}
      </div>
    </motion.button>
  );
};

const SortableSkillTile = ({ skill, onSelect, isFav, onToggleFav }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: skill.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : 'auto',
    touchAction: 'manipulation',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <SkillTile skill={skill} onSelect={onSelect} dragging={isDragging} isFav={isFav} onToggleFav={onToggleFav} />
    </div>
  );
};

// ---------- main view ----------
const ORDER_KEY = 'nexa_skill_order_v1';
const DEFAULT_ORDER = ['health', 'calendar', 'memory', 'personality', 'nexa', 'settings', 'kids', 'work', 'business', 'styling', 'personal'];

export const LifeView = () => {
  const [today, setToday] = useState(null);
  const [weights, setWeights] = useState([]);
  const [days, setDays] = useState([]);
  const [nutrition, setNutrition] = useState(null);
  const [nutritionHistory, setNutritionHistory] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [memoryCount, setMemoryCount] = useState(0);
  const [calendar, setCalendar] = useState(null);
  const [personality, setPersonality] = useState(null);
  const [wardrobeCount, setWardrobeCount] = useState(0);
  const [kids, setKids] = useState([]);
  const [skillId, setSkillId] = useState(() => {
    const m = /^#life\/([\w-]+)/.exec(window.location.hash);
    return m ? m[1] : null;
  });
  const [subPath, setSubPath] = useState(() => {
    const m = /^#life\/[\w-]+\/([\w-]+)/.exec(window.location.hash);
    return m ? m[1] : null;
  });
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(ORDER_KEY) || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {
      /* ignore parse errors */
    }
    return DEFAULT_ORDER;
  });
  const [favorites, setFavorites] = useState([]);
  useEffect(() => {
    nexaApi.get('/life/favorites').then(({ data }) => setFavorites(data.ids || [])).catch(() => {});
  }, []);
  const toggleFav = (id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      nexaApi.put('/life/favorites', { ids: next }).catch(() => {});
      return next;
    });
  };

  const load = () => {
    nexaApi.get('/today').then(({ data }) => setToday(data));
    nexaApi.get('/weights', { params: { days: 90 } }).then(({ data }) => setWeights(data.weights));
    nexaApi.get('/health', { params: { days: 30 } }).then(({ data }) => setDays(data.days.slice().reverse()));
    nexaApi.get('/nutrition/today').then(({ data }) => setNutrition(data));
    nexaApi.get('/nutrition/history', { params: { days: 7 } }).then(({ data }) => setNutritionHistory(data)).catch(() => {});
    nexaApi.get('/workouts', { params: { days: 365 } }).then(({ data }) => setWorkouts(data.workouts));
    nexaApi.get('/memories').then(({ data }) => setMemoryCount(data.memories.length));
    nexaApi.get('/personality').then(({ data }) => setPersonality(data)).catch(() => {});
    nexaApi.get('/wardrobe').then(({ data }) => setWardrobeCount(data.items.length)).catch(() => {});
    nexaApi.get('/kids').then(({ data }) => setKids(data.kids)).catch(() => {});
    nexaApi.get('/google/upcoming', { params: { days: 30 } })
      .then(({ data }) => setCalendar(data))
      .catch(() => setCalendar({ connected: false, events: [] }));
  };

  useEffect(() => {
    load();
    const refresh = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    const onHash = () => {
      const m = /^#life\/([\w-]+)/.exec(window.location.hash);
      setSkillId(m ? m[1] : null);
      const sm = /^#life\/[\w-]+\/([\w-]+)/.exec(window.location.hash);
      setSubPath(sm ? sm[1] : null);
    };
    window.addEventListener('hashchange', onHash);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  const skills = useMemo(
    () => buildSkills({ today, weights, days, workouts, memoryCount, calendar, personality, wardrobeCount, kids }),
    [today, weights, days, workouts, memoryCount, calendar, personality, wardrobeCount, kids],
  );
  const skillMap = useMemo(
    () => Object.fromEntries(skills.map((s) => [s.id, s])),
    [skills],
  );
  const orderedIds = useMemo(() => {
    const known = order.filter((id) => skillMap[id]);
    const missing = skills.map((s) => s.id).filter((id) => !known.includes(id));
    const base = [...known, ...missing];
    // Favorites float to the top (keeping their relative order).
    const favs = base.filter((id) => favorites.includes(id));
    const rest = base.filter((id) => !favorites.includes(id));
    return [...favs, ...rest];
  }, [skills, skillMap, order, favorites]);
  const activeSkill = skillId ? skillMap[skillId] : null;

  // Long-press to drag; short tap opens the tile.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(active.id);
    const newIndex = orderedIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    setOrder(next);
    localStorage.setItem(ORDER_KEY, JSON.stringify(next));
  };

  const openSkill = (s) => {
    if (!s.unlocked) {
      toast(`${s.name} isn't tracked yet.`, {
        description: `Tell NEXA in chat to start tracking ${s.name.toLowerCase()} — she'll unlock this tile.`,
      });
      return;
    }
    window.location.hash = `#life/${s.id}`;
    setSkillId(s.id);
  };

  const closeSkill = () => {
    if (window.location.hash.startsWith('#life/')) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    setSkillId(null);
    setSubPath(null);
  };

  const openBusinessLevanta = () => {
    window.location.hash = '#life/business/levanta';
    setSubPath('levanta');
  };

  const closeBusinessLevanta = () => {
    window.location.hash = '#life/business';
    setSubPath(null);
  };

  if (!today) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (activeSkill && activeSkill.unlocked) {
    return (
      <motion.div
        key={activeSkill.id}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="flex flex-col gap-6 lg:max-w-4xl lg:mx-auto lg:px-10 lg:py-10 lg:w-full"
        data-testid={`nexa-life-detail-${activeSkill.id}`}
      >
        <button
          onClick={closeSkill}
          data-testid="nexa-life-back"
          className="lg:hidden self-start flex items-center gap-1.5 text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 hover:text-sky-400 active:scale-95 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Life · Skills
        </button>
        <div>
          <h1 className="nexa-heading text-3xl font-light tracking-tight">{activeSkill.name}</h1>
        </div>

        {activeSkill.id === 'health' && (
          <HealthFitnessDetail today={today} days={days} weights={weights}
            workouts={workouts} nutrition={nutrition} nutritionHistory={nutritionHistory}
            reloadWorkouts={() => nexaApi.get('/workouts', { params: { days: 365 } }).then(({ data }) => setWorkouts(data.workouts)).catch(() => {})} />
        )}
        {activeSkill.id === 'calendar' && <CalendarDetail />}
        {activeSkill.id === 'personality' && <PersonalityDetail profile={personality} />}
        {activeSkill.id === 'nexa' && <NexaMindView />}
        {activeSkill.id === 'journal' && <JournalDetail />}
        {activeSkill.id === 'finance' && <FinanceDetail />}
        {activeSkill.id === 'styling' && <StylingDetail personality={personality} />}
        {activeSkill.id === 'memory' && <MemoryView />}
        {activeSkill.id === 'kids' && <KidsDetail />}
        {activeSkill.id === 'settings' && <SettingsDetail />}
        {activeSkill.id === 'personal' && <GrowthDetail />}
        {activeSkill.id === 'business' && (
          subPath === 'levanta'
            ? <BusinessLevantaDetail onBack={closeBusinessLevanta} />
            : <BusinessDetail onOpenLevanta={openBusinessLevanta} />
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-6 lg:max-w-[1600px] lg:mx-auto lg:px-10 xl:px-14 lg:py-10 lg:w-full"
      data-testid="nexa-life-view"
    >
      <div>
        <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.3em] text-sky-400/80 uppercase">
          <span className="w-4 h-px bg-sky-400/50" /> Life · Skills
        </div>
        <h1 className="nexa-heading text-3xl lg:text-5xl font-light tracking-tight mt-2">Everything NEXA knows</h1>
        <p className="text-white/40 text-sm mt-1">
          Tap a skill to open it. Long-press and drag to rearrange. Locked skills unlock as you
          tell NEXA what to track.
        </p>
      </div>

      <AnimatePresence>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-6" data-testid="nexa-skills-grid">
              {orderedIds.map((id) => (
                <SortableSkillTile key={id} skill={skillMap[id]} onSelect={openSkill}
                  isFav={favorites.includes(id)} onToggleFav={toggleFav} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </AnimatePresence>

      <p className="text-[11px] text-white/30 text-center px-6 font-mono tracking-wider">
        {skills.filter((s) => s.unlocked).length}/{skills.length} SKILLS ACTIVE
      </p>
    </motion.div>
  );
};
